import React, { useState } from "react";
import Auth from "../../Auth";
import styled from "styled-components/macro";

const Container = styled.div`
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
`;

const RequestItem = styled.div`
  margin-bottom: 10px;
  padding: 10px;
  border: 1px solid #eee;
  border-radius: 4px;
  background: ${(props) => (props.success ? "#f0fff0" : "#fff0f0")};
`;

const Button = styled.button`
  background: #4a4a4a;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 10px;
  &:hover {
    background: #333;
  }
`;

const Heading = styled.h2`
  margin-top: 0;
`;

const Pre = styled.pre`
  background: #f5f5f5;
  padding: 10px;
  border-radius: 4px;
  overflow-x: auto;
  max-height: 200px;
  overflow-y: auto;
`;

const TestSequence = [
  {
    name: "Initial Request",
    path: "/api/ai/brainstorm",
    body: {
      prompt: "Things a young cop in Atlanta would say when venting to a friend",
      context: "The character has a cynical view of police work",
      n: 5,
      items: [],
      user_starters: [],
      user_rejected: [],
    },
  },
  {
    name: "Follow-up with Kept Items",
    path: "/api/ai/brainstorm",
    body: {
      prompt: "Things a young cop in Atlanta would say when venting to a friend",
      context: "The character has a cynical view of police work",
      n: 3,
      items: ["Man, that was a long day", "You ever feel like it's exhausting just being alive?"],
      user_starters: [],
      user_rejected: [
        "My sergeant keeps saying I'll get used to it. I don't think I want to get used to it, you know?",
        "Sometimes I stare at my uniform before putting it on and wonder if I'm making any difference at all.",
      ],
    },
  },
  {
    name: "Refresh Request",
    path: "/api/ai/brainstorm",
    body: {
      prompt: "Things a young cop in Atlanta would say when venting to a friend",
      context: "The character has a cynical view of police work",
      n: 5,
      items: [],
      user_starters: [],
      user_rejected: [
        "My sergeant keeps saying I'll get used to it. I don't think I want to get used to it, you know?",
        "Sometimes I stare at my uniform before putting it on and wonder if I'm making any difference at all.",
        "The worst part isn't the danger or the hours—it's watching people have their worst day ever, and all you can do is write it down.",
      ],
    },
  },
];

export const BrainstormNetworkTest = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentTest, setCurrentTest] = useState(0);

  const runTest = async (test) => {
    setLoading(true);

    try {
      const startTime = Date.now();
      const result = await Auth.fetchWithIdToken(test.path, test.body);
      const endTime = Date.now();

      setResults((prev) => [
        ...prev,
        {
          name: test.name,
          success: true,
          requestBody: test.body,
          response: result,
          requestTime: endTime - startTime,
          timestamp: new Date().toISOString(),
        },
      ]);
    } catch (error) {
      setResults((prev) => [
        ...prev,
        {
          name: test.name,
          success: false,
          requestBody: test.body,
          error: error.message || "Unknown error",
          timestamp: new Date().toISOString(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const runNextTest = () => {
    if (currentTest < TestSequence.length) {
      runTest(TestSequence[currentTest]);
      setCurrentTest((prev) => prev + 1);
    }
  };

  const runAllTests = async () => {
    setResults([]);
    setCurrentTest(0);

    for (let i = 0; i < TestSequence.length; i++) {
      setCurrentTest(i);
      await runTest(TestSequence[i]);
    }
  };

  return (
    <Container>
      <Heading>Brainstorm Network Test</Heading>
      <div>
        <Button
          onClick={runNextTest}
          disabled={loading || currentTest >= TestSequence.length}
        >
          {loading ? "Running..." : "Run Next Test"}
        </Button>
        <Button
          onClick={runAllTests}
          disabled={loading}
        >
          Run All Tests
        </Button>
      </div>

      <div style={{ marginTop: 20 }}>
        <h3>Results:</h3>
        {results.length === 0 ? (
          <p>No tests run yet</p>
        ) : (
          results.map((result, index) => (
            <RequestItem
              key={index}
              success={result.success}
            >
              <h4>
                {result.name} - {result.success ? "✅ Success" : "❌ Failed"}
              </h4>
              {result.requestTime && <p>Request time: {result.requestTime}ms</p>}
              <p>Timestamp: {result.timestamp}</p>

              <details>
                <summary>Request Body</summary>
                <Pre>{JSON.stringify(result.requestBody, null, 2)}</Pre>
              </details>

              {result.success ? (
                <details>
                  <summary>Response (Items: {result.response.items?.length || 0})</summary>
                  <Pre>{JSON.stringify(result.response, null, 2)}</Pre>
                </details>
              ) : (
                <details>
                  <summary>Error</summary>
                  <Pre>{result.error}</Pre>
                </details>
              )}
            </RequestItem>
          ))
        )}
      </div>
    </Container>
  );
};

export default BrainstormNetworkTest;
